import type { LoginResponse } from '@ais-ui/api-spec/oceanio';

/**
 * A logged-in session. Only the access token is kept — never the password —
 * so the user logs in again when it expires (see DAAS-6819 for refresh tokens).
 */
export interface Session {
  userId: string;
  token: string;
  /** Epoch milliseconds after which the token is no longer valid. */
  expiresAt: number;
}

const STORAGE_KEY = 'oceanio.session';
const LEGACY_KEYS = ['credentials', 'access_token'];
const DEFAULT_LIFETIME_MS = 60 * 60 * 1000;
const EPOCH_SECONDS_THRESHOLD = 1e9;

/** `expires` may be an ISO date, epoch seconds or seconds from now. */
export const parseExpiry = (expires: string | undefined, now: number) => {
  if (!expires) {
    return now + DEFAULT_LIFETIME_MS;
  }

  const seconds = Number(expires);
  if (Number.isFinite(seconds)) {
    return seconds > EPOCH_SECONDS_THRESHOLD
      ? seconds * 1000
      : now + seconds * 1000;
  }

  const date = Date.parse(expires);
  return Number.isNaN(date) ? now + DEFAULT_LIFETIME_MS : date;
};

export const toSession = (
  userId: string,
  { token, expires }: LoginResponse,
  now = Date.now(),
): Session => {
  if (!token) {
    throw new Error('Login response did not include a token');
  }
  return { userId, token, expiresAt: parseExpiry(expires, now) };
};

export const isExpired = (session: Session, now = Date.now()) =>
  session.expiresAt <= now;

const isSession = (value: unknown): value is Session =>
  typeof value === 'object' &&
  value != null &&
  typeof (value as Session).userId === 'string' &&
  typeof (value as Session).token === 'string' &&
  typeof (value as Session).expiresAt === 'number';

export const readStoredSession = (): Session | undefined => {
  // Older versions stored the plain-text password; make sure it is gone.
  LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));

  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ?? 'null',
    );
    return isSession(stored) && !isExpired(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
};

export const storeSession = (session: Session) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

export const clearStoredSession = () => localStorage.removeItem(STORAGE_KEY);
